import { useState, useEffect, ChangeEvent, Dispatch, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { Button } from 'UIPackage/MainModule/components/Button';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { KnmDocumentsModalTable } from 'KnmPackage/KnmDocumentsModule/components/KnmDocumentsModalTable';
import { IKnmDocument } from 'APIPackage/KnmModule/interfaces/IKnmDocument';
import { TableCheckItems } from 'CorePackage/MainModule/types/TableCheckItems';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';

import { IKnmAttachmentWithType } from '../interfaces/IKnmAttachmentWithType';
import styles from './KnmDocumentsModal.module.scss';
import { knmDocumentsDownload } from '../actions/knmDocumentsActions';

interface IKnmDocumentsModalProps {
  isVisibleModal: boolean;
  knmDocuments?: IKnmDocument[] | null;
  onCloseModal: () => void;
}

export const KnmDocumentsModal = ({ isVisibleModal, knmDocuments, onCloseModal }: IKnmDocumentsModalProps) => {
  const [documents, setDocuments] = useState<IKnmAttachmentWithType[]>([]);
  const [itemsCheck, setItemsCheck] = useState<TableCheckItems>({});
  const [isCheckboxAllChecked, setIsCheckboxAllChecked] = useState<boolean>(false);

  const dispatch = useDispatch<Dispatch<RootActions>>();

  useEffect(() => {
    if (knmDocuments && knmDocuments.length) {
      const newItemsCheck: TableCheckItems = {};
      const result: IKnmAttachmentWithType[] = [];

      knmDocuments.forEach(item => {
        const res = item.attachments?.map(at => ({
          ...at,
          type: item.type?.name ?? '',
        }));

        if (res !== undefined) {
          result.push(...res);
        }
      });

      result.forEach(item => {
        if (item.guid) {
          newItemsCheck[item.guid] = false;
        }
      });

      setDocuments(result);
      setItemsCheck(newItemsCheck);
    }
  }, [knmDocuments]);

  useEffect(() => {
    const keysArray = Object.keys(itemsCheck);
    const isEachItemTrue = keysArray.length ? keysArray.every(key => itemsCheck[key]) : false;

    setIsCheckboxAllChecked(isEachItemTrue);
  }, [itemsCheck]);

  const handleTableCheckboxAllChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;

    setItemsCheck((prev: TableCheckItems) => {
      const newItemsCheck: TableCheckItems = {};

      Object.keys(prev).forEach(key => {
        newItemsCheck[key] = isChecked;
      });

      return newItemsCheck;
    });
  }, []);

  const handleAfterCloseModal = useCallback(() => {
    setItemsCheck((prev: TableCheckItems) => {
      const newItemsCheck: TableCheckItems = {};

      Object.keys(prev).forEach(key => {
        newItemsCheck[key] = false;
      });

      return newItemsCheck;
    });
  }, []);

  const handleTableCheckboxChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setItemsCheck({
        ...itemsCheck,
        [e.target.id]: e.target.checked,
      });
    },
    [itemsCheck],
  );

  const handleDownloadButton = useCallback(() => {
    dispatch(knmDocumentsDownload(documents, itemsCheck));
  }, [dispatch, documents, itemsCheck]);

  return (
    <Modal visible={isVisibleModal} onClose={onCloseModal} afterClose={handleAfterCloseModal}>
      <div className={styles.DocumentsModalContainer}>
        <h2 className={styles.Title}>Список сопроводительных документов</h2>
        <Button
          className={styles.DownloadButton}
          text="Загрузить"
          id="uploadButton"
          color="primary"
          onClick={handleDownloadButton}
        />
        <KnmDocumentsModalTable
          documents={documents}
          itemsCheck={itemsCheck}
          isCheckboxAllChecked={isCheckboxAllChecked}
          handleTableCheckboxAllChange={handleTableCheckboxAllChange}
          handleTableCheckboxChange={handleTableCheckboxChange}
        />
      </div>
    </Modal>
  );
};
